import { useStore } from 'react-redux';
import { RootState } from '@/store/store';
import { selectExtensionInfo, selectFlagsConfig } from '@/store/selectors';
import { useFeatureFlag } from '@/hooks/config/useFeatureFlag.hook';
import { useFlagsConfig } from '@/hooks/config/useFlagsConfig.hook';
import { logger } from '@/utils/logger/logger';
import { OptInPreference } from '@/constants/features/opt-in';
import { usePermissionsAnalytics } from './analytics/usePermissionsAnalytics.hook';
import { resolveConsentVariant } from './logic/resolve-consent-variant';
import { findTermsCheckboxId } from './logic/stored-disclosures';

// Completing register/login accepts the disclaimer's T&C, so we apply the same
// effect as ticking the variant's terms checkbox in the consent modal. First-time
// only (eulaAgreed guards it) and additive: it never removes a bucket.
export const useAcceptTermsOnAuth = () => {
  const store = useStore<RootState>();
  const { getFeatureValue, updateAttributes } = useFeatureFlag();
  const { setConsent, updateFlagConfigSilently } = useFlagsConfig();
  const { firePermissionsEvents } = usePermissionsAnalytics();

  const acceptTermsOnAuth = async (activationSource: string): Promise<void> => {
    try {
      // read at call-time so we see the freshly-synced consent, not a render closure
      const flags = selectFlagsConfig(store.getState());
      if (flags.eulaAgreed === true) {
        return;
      }

      // publish fresh consent attributes before resolving, so the variant is
      // decided against the synced state rather than the async effect's timing.
      // same shape as useConsentAttributesSync — auth attributes are not ours.
      await updateAttributes({
        eulaAgreed: Boolean(flags.eulaAgreed),
        optInPreferences: Array.isArray(flags.optInPreferences)
          ? flags.optInPreferences
          : null,
        onNewOptInSchema: flags.onNewOptInSchema === true,
      });

      const { variant } = resolveConsentVariant(
        flags,
        selectExtensionInfo(store.getState()),
        getFeatureValue
      );

      const termsCheckboxId = variant && findTermsCheckboxId(variant);
      let added: OptInPreference[] = [];
      if (variant && termsCheckboxId) {
        added = (await setConsent(variant, [termsCheckboxId], true)) ?? [];
      } else {
        await updateFlagConfigSilently({
          eulaAgreed: true,
          isPrivacyAgreed: true,
          mandatoryButtonClicked: true,
        });
      }

      if (added.length > 0) {
        firePermissionsEvents(activationSource, added, true);
      }
    } catch (error) {
      logger.error('acceptTermsOnAuth failed', error);
    }
  };

  return { acceptTermsOnAuth };
};
